import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from './components/ChangePasswordModal';
import AdminRevisionChat from './components/AdminRevisionChat';
import CustomersPanel from './components/CustomersPanel';
import SurveysPanel from './components/SurveysPanel';
import { supabase } from '../../lib/supabase';

interface CustomerProfile {
  shipping_phone?: string;
  shipping_address?: string;
  shipping_city?: string;
  shipping_district?: string;
  shipping_postal_code?: string;
  billing_same_as_shipping?: boolean;
  billing_name?: string;
  billing_address?: string;
  billing_city?: string;
  billing_district?: string;
  billing_postal_code?: string;
  billing_tax_office?: string;
  billing_tax_number?: string;
}

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  order_title: string;
  order_description: string;
  status: string;
  file_urls: string[];
  created_at: string;
  phone?: string;
  company?: string;
  admin_note?: string;
  approved_design_url?: string;
  tracking_number?: string;
  shipping_company?: string;
  customer_profile?: CustomerProfile | null;
  design_approved_by_admin?: boolean;
  design_approved_by_customer?: boolean;
  customer_approval_date?: string;
  price?: number;
  product_title?: string;
  product_description?: string;
  payment_notified?: boolean;
  payment_notified_at?: string;
  payment_confirmed?: boolean;
  payment_confirmed_at?: string;
  paid?: boolean;
}

interface Quote {
  id: string;
  admin_email: string;
  title: string;
  description: string;
  items: Array<{
    name: string;
    description?: string;
    quantity: number;
    unit_price: number;
    total: number;
  }>;
  total_price: number;
  valid_until: string | null;
  share_id: string;
  customer_email: string | null;
  status: 'draft' | 'sent' | 'accepted' | 'rejected';
  created_at: string;
  updated_at: string;
}

interface Recommendation {
  id: string;
  admin_email: string;
  title: string;
  description: string;
  price: number;
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

const AdminDashboard = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLiveSupport, setShowLiveSupport] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showDelivered, setShowDelivered] = useState(false);
  const [liveSupportLoading, setLiveSupportLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<Order | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationDescription, setNotificationDescription] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationSuccess, setNotificationSuccess] = useState(false);
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [designFile, setDesignFile] = useState<File | null>(null);
  const [savingDesign, setSavingDesign] = useState(false);
  const [deleteDesignConfirm, setDeleteDesignConfirm] = useState(false);
  const [deletingDesign, setDeletingDesign] = useState(false);
  const [adminApproveDesign, setAdminApproveDesign] = useState(true);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [savingTracking, setSavingTracking] = useState(false);
  const [shippingCompany, setShippingCompany] = useState('');
  const navigate = useNavigate();
  const adminEmail = localStorage.getItem('adminEmail');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedOrderForImage, setSelectedOrderForImage] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [orderPrice, setOrderPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [productTitle, setProductTitle] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [rejectingPayment, setRejectingPayment] = useState(false);
  const [deliveringOrder, setDeliveringOrder] = useState(false);
  const [expandedDeliveredId, setExpandedDeliveredId] = useState<string | null>(null);
  const [deleteDeliveredConfirm, setDeleteDeliveredConfirm] = useState<Order | null>(null);
  const [deletingDelivered, setDeletingDelivered] = useState(false);
  const [showSurveys, setShowSurveys] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [quoteDescription, setQuoteDescription] = useState('');
  const [quoteItems, setQuoteItems] = useState<Array<{
    name: string;
    description?: string;
    quantity: number;
    unit_price: number;
    total: number;
  }>>([{ name: '', description: '', quantity: 1, unit_price: 0, total: 0 }]);
  const [quoteValidUntil, setQuoteValidUntil] = useState('');
  const [quoteCustomerEmail, setQuoteCustomerEmail] = useState('');
  const [savingQuote, setSavingQuote] = useState(false);
  const [deleteQuoteConfirm, setDeleteQuoteConfirm] = useState<Quote | null>(null);
  const [deletingQuote, setDeletingQuote] = useState(false);
  const [copiedQuoteId, setCopiedQuoteId] = useState<string | null>(null);

  // Yeni state'ler - Öneriler için
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [editingRecommendation, setEditingRecommendation] = useState<Recommendation | null>(null);
  const [recommendationTitle, setRecommendationTitle] = useState('');
  const [recommendationDescription, setRecommendationDescription] = useState('');
  const [recommendationPrice, setRecommendationPrice] = useState('');
  const [recommendationImageFile, setRecommendationImageFile] = useState<File | null>(null);
  const [savingRecommendation, setSavingRecommendation] = useState(false);
  const [deleteRecommendationConfirm, setDeleteRecommendationConfirm] = useState<Recommendation | null>(null);
  const [deletingRecommendation, setDeletingRecommendation] = useState(false);

  useEffect(() => {
    if (!adminEmail) {
      navigate('/admin-login');
      return;
    }
    fetchOrders();
  }, [adminEmail, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getAll', adminEmail }),
        }
      );

      const data = await response.json();
      if (data.orders) {
        setOrders(data.orders);
        if (data.orders.length > 0 && !selectedOrder) {
          setSelectedOrder(data.orders[0]);
        }
      }
    } catch (error) {
      console.error('Siparişler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getAllQuotes', adminEmail }),
        }
      );

      const data = await response.json();
      if (data.quotes) {
        setQuotes(data.quotes);
      }
    } catch (error) {
      console.error('Teklifler yüklenirken hata:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  useEffect(() => {
    if (showQuotes && adminEmail) {
      fetchQuotes();
    }
  }, [showQuotes, adminEmail]);

  useEffect(() => {
    if (showRecommendations && adminEmail) {
      fetchRecommendations();
    }
  }, [showRecommendations, adminEmail]);

  const calculateQuoteTotal = (items: typeof quoteItems) => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };

  const handleQuoteItemChange = (index: number, field: string, value: string | number) => {
    const newItems = [...quoteItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setQuoteItems(newItems);
  };

  const addQuoteItem = () => {
    setQuoteItems([...quoteItems, { name: '', description: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const removeQuoteItem = (index: number) => {
    if (quoteItems.length > 1) {
      setQuoteItems(quoteItems.filter((_, i) => i !== index));
    }
  };

  const openQuoteModal = (quote?: Quote) => {
    if (quote) {
      setEditingQuote(quote);
      setQuoteTitle(quote.title);
      setQuoteDescription(quote.description);
      setQuoteItems(quote.items);
      setQuoteValidUntil(quote.valid_until ? quote.valid_until.split('T')[0] : '');
      setQuoteCustomerEmail(quote.customer_email || '');
    } else {
      setEditingQuote(null);
      setQuoteTitle('');
      setQuoteDescription('');
      setQuoteItems([{ name: '', description: '', quantity: 1, unit_price: 0, total: 0 }]);
      setQuoteValidUntil('');
      setQuoteCustomerEmail('');
    }
    setShowQuoteModal(true);
  };

  const handleSaveQuote = async () => {
    if (!quoteTitle.trim() || quoteItems.some(item => !item.name.trim())) {
      alert('Lütfen teklif başlığını ve tüm ürün isimlerini doldurun');
      return;
    }

    setSavingQuote(true);
    try {
      const totalPrice = calculateQuoteTotal(quoteItems);
      const action = editingQuote ? 'updateQuote' : 'createQuote';
      
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action,
            quoteId: editingQuote?.id,
            adminEmail,
            title: quoteTitle.trim(),
            description: quoteDescription.trim(),
            items: quoteItems,
            totalPrice,
            validUntil: quoteValidUntil || null,
            customerEmail: quoteCustomerEmail.trim() || null,
          }),
        }
      );

      const data = await response.json();
      if (data.success || data.quote) {
        await fetchQuotes();
        setShowQuoteModal(false);
        alert(editingQuote ? '✅ Teklif güncellendi!' : '✅ Teklif oluşturuldu!');
      }
    } catch (error) {
      console.error('Teklif kaydedilirken hata:', error);
      alert('Teklif kaydedilirken bir hata oluştu');
    } finally {
      setSavingQuote(false);
    }
  };

  const handleDeleteQuote = async (quoteId: string) => {
    setDeletingQuote(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'deleteQuote',
            quoteId,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        await fetchQuotes();
        setDeleteQuoteConfirm(null);
        alert('✅ Teklif silindi!');
      }
    } catch (error) {
      console.error('Teklif silinirken hata:', error);
      alert('Teklif silinirken bir hata oluştu');
    } finally {
      setDeletingQuote(false);
    }
  };

  const copyQuoteLink = (shareId: string) => {
    const link = `https://xkartvizitler.com/siparis-takibi/quote/${shareId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedQuoteId(shareId);
      setTimeout(() => setCopiedQuoteId(null), 2000);
    });
  };

  const getQuoteStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Taslak',
      sent: 'Gönderildi',
      accepted: 'Kabul Edildi',
      rejected: 'Reddedildi',
    };
    return labels[status] || status;
  };

  const getQuoteStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'updateStatus',
            orderId,
            status: newStatus,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId ? { ...order, status: newStatus } : order
          )
        );
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(prev =>
            prev
              ? {
                  ...prev,
                  status: newStatus,
                }
              : null
          );
        }
        await fetchOrders();
      }
    } catch (error) {
      console.error('Durum güncellenirken hata:', error);
    }
  };

  const handleDeleteDeliveredOrder = async (orderId: string) => {
    setDeletingDelivered(true);
    try {
      // Önce ilişkili kayıtları temizle
      await supabase.from('cart').delete().eq('order_id', orderId);
      await supabase.from('notifications').delete().eq('order_id', orderId);
      await supabase.from('design_revisions').delete().eq('order_id', orderId);
      await supabase.from('reviews').delete().eq('order_id', orderId);

      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'delete',
            orderId,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(null);
        }
        setDeleteDeliveredConfirm(null);
        alert('✅ Teslim edilen sipariş başarıyla silindi!');
      } else {
        throw new Error(data.error || 'Sipariş silinemedi');
      }
    } catch (error) {
      console.error('Sipariş silinirken hata:', error);
      alert('❌ Sipariş silinirken bir hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setDeletingDelivered(false);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    setDeleting(true);
    try {
      // Önce ilişkili kayıtları temizle
      // 1. Sepet kayıtlarını sil
      await supabase
        .from('cart')
        .delete()
        .eq('order_id', orderId);

      // 2. Bildirimleri sil
      await supabase
        .from('notifications')
        .delete()
        .eq('order_id', orderId);

      // 3. Tasarım revizyonlarını sil
      await supabase
        .from('design_revisions')
        .delete()
        .eq('order_id', orderId);

      // 4. Değerlendirmeleri sil
      await supabase
        .from('reviews')
        .delete()
        .eq('order_id', orderId);

      // 5. Şimdi siparişi sil
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'delete',
            orderId,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(null);
        }
        setDeleteConfirm(null);
        alert('✅ Sipariş başarıyla silindi!');
      } else {
        throw new Error(data.error || 'Sipariş silinemedi');
      }
    } catch (error) {
      console.error('Sipariş silinirken hata:', error);
      alert('❌ Sipariş silinirken bir hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminEmail');
    navigate('/admin-login');
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Tasarım Bilgileriniz Bekleniyor',
      designer_review: 'Grafiker Kontrolünde',
      design_in_progress: 'Tasarım Hazırlanıyor',
      awaiting_approval: 'Tasarım Onayınız Bekleniyor',
      sent_to_print: 'Üretimde',
      shipped: 'Ürün Kargolandı',
      cancelled: 'İptal',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-amber-500',
      designer_review: 'bg-indigo-500',
      design_in_progress: 'bg-cyan-500',
      awaiting_approval: 'bg-orange-500',
      sent_to_print: 'bg-teal-500',
      shipped: 'bg-emerald-500',
      cancelled: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredOrders = orders.filter(order => {
    // Teslim edilenler ve kargolandılar normal listede gözükmeyecek
    if (order.status === 'delivered' || order.status === 'shipped') return false;
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return order.status === 'pending';
    if (activeTab === 'payment_notified')
      return order.payment_notified && !order.payment_confirmed;
    if (activeTab === 'in_progress')
      return ['designer_review', 'design_in_progress', 'awaiting_approval', 'sent_to_print'].includes(order.status);
    return order.status === activeTab;
  });

  const deliveredOrders = orders.filter(order => order.status === 'delivered' || order.status === 'shipped');

  const stats = {
    total: orders.filter(o => o.status !== 'delivered' && o.status !== 'shipped').length,
    pending: orders.filter(o => o.status === 'pending').length,
    paymentNotified: orders.filter(o => o.payment_notified && !o.payment_confirmed).length,
    inProgress: orders.filter(o => ['designer_review', 'design_in_progress', 'awaiting_approval', 'sent_to_print'].includes(o.status)).length,
    completed: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered' || o.status === 'shipped').length,
  };

  const handleSaveNote = async () => {
    if (!selectedOrder) return;

    setSavingNote(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'updateNote',
            orderId: selectedOrder.id,
            adminNote: noteText,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === selectedOrder.id ? { ...order, admin_note: noteText } : order
          )
        );
        setSelectedOrder(prev =>
          prev
            ? {
                ...prev,
                admin_note: noteText,
              }
            : null
        );
        setShowNoteModal(false);
      }
    } catch (error) {
      console.error('Not kaydedilirken hata:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const openNoteModal = () => {
    setNoteText(selectedOrder?.admin_note || '');
    setShowNoteModal(true);
  };

  const openNotificationModal = () => {
    setNotificationTitle('');
    setNotificationDescription('');
    setNotificationSuccess(false);
    setShowNotificationModal(true);
  };

  const handleSendNotification = async () => {
    if (!selectedOrder || !notificationTitle.trim() || !notificationDescription.trim()) {
      return;
    }

    setSendingNotification(true);

    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          customer_email: selectedOrder.customer_email,
          title: notificationTitle.trim(),
          description: notificationDescription.trim(),
          order_id: selectedOrder.id,
          is_read: false,
        });

      if (error) throw error;

      setNotificationSuccess(true);
      setTimeout(() => {
        setShowNotificationModal(false);
        setNotificationSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Bildirim gönderilirken hata:', error);
    } finally {
      setSendingNotification(false);
    }
  };

  const handleSaveDesign = async () => {
    if (!selectedOrder || !designFile) return;

    setSavingDesign(true);
    try {
      const fileExt = designFile.name.split('.').pop();
      const fileName = `${selectedOrder.id}-${Date.now()}.${fileExt}`;

      if (selectedOrder.approved_design_url) {
        const urlParts = selectedOrder.approved_design_url.split('/');
        const oldFileName = urlParts[urlParts.length - 1];
        try {
          await supabase.storage.from('approved-designs').remove([oldFileName]);
        } catch (err) {
          console.log('Eski dosya silinemedi:', err);
        }
      }

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('approved-designs')
        .upload(fileName, designFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('Yükleme hatası:', uploadError);
        throw new Error(`Dosya yüklenemedi: ${uploadError.message}`);
      }

      const { data: { publicUrl } } = supabase.storage
        .from('approved-designs')
        .getPublicUrl(fileName);

      if (!publicUrl) {
        throw new Error('Public URL alınamadı');
      }

      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'updateDesign',
            orderId: selectedOrder.id,
            approvedDesignUrl: publicUrl,
            designApprovedByAdmin: adminApproveDesign,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === selectedOrder.id
              ? {
                  ...order,
                  approved_design_url: publicUrl,
                  design_approved_by_admin: adminApproveDesign,
                  design_approved_by_customer: false,
                  customer_approval_date: undefined,
                }
              : order
          )
        );
        setSelectedOrder(prev =>
          prev
            ? {
                ...prev,
                approved_design_url: publicUrl,
                design_approved_by_admin: adminApproveDesign,
                design_approved_by_customer: false,
                customer_approval_date: undefined,
              }
            : null
        );
        setShowDesignModal(false);
        setDesignFile(null);
        setAdminApproveDesign(true);
      } else {
        throw new Error(data.error || 'Veritabanı güncellenemedi');
      }
    } catch (error) {
      console.error('Tasarım kaydedilirken hata:', error);
    } finally {
      setSavingDesign(false);
    }
  };

  const handleSavePrice = async () => {
    if (!selectedOrder) return;

    const priceValue = parseFloat(orderPrice);
    if (isNaN(priceValue) || priceValue < 0) {
      alert('Lütfen geçerli bir fiyat girin');
      return;
    }

    setSavingPrice(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'updatePrice',
            orderId: selectedOrder.id,
            price: priceValue,
            productTitle: productTitle.trim(),
            productDescription: productDescription.trim(),
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === selectedOrder.id
              ? {
                  ...order,
                  price: priceValue,
                  product_title: productTitle.trim(),
                  product_description: productDescription.trim(),
                }
              : order
          )
        );
        setSelectedOrder(prev =>
          prev
            ? {
                ...prev,
                price: priceValue,
                product_title: productTitle.trim(),
                product_description: productDescription.trim(),
              }
            : null
        );
        alert('Ürün bilgileri başarıyla kaydedildi!');
      }
    } catch (error) {
      console.error('Fiyat kaydedilirken hata:', error);
      alert('Fiyat kaydedilirken bir hata oluştu');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleDeleteDesign = async () => {
    if (!selectedOrder) return;

    setDeletingDesign(true);
    try {
      if (selectedOrder.approved_design_url) {
        const urlParts = selectedOrder.approved_design_url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        await supabase.storage.from('approved-designs').remove([fileName]);
      }

      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'updateDesign',
            orderId: selectedOrder.id,
            approvedDesignUrl: null,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === selectedOrder.id ? { ...order, approved_design_url: undefined } : order
          )
        );
        setSelectedOrder(prev =>
          prev ? { ...prev, approved_design_url: undefined } : null
        );
        setDeleteDesignConfirm(false);
      }
    } catch (error) {
      console.error('Tasarım silinirken hata:', error);
    } finally {
      setDeletingDesign(false);
    }
  };

  const handleSaveTracking = async () => {
    if (!selectedOrder) return;

    if (!trackingNumber.trim()) {
      alert('Lütfen takip numarası girin');
      return;
    }

    setSavingTracking(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'updateTracking',
            orderId: selectedOrder.id,
            trackingNumber: trackingNumber.trim(),
            shippingCompany: shippingCompany.trim(),
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === selectedOrder.id
              ? { ...order, tracking_number: trackingNumber.trim(), shipping_company: shippingCompany.trim() }
              : order
          )
        );
        setSelectedOrder(prev =>
          prev
            ? { ...prev, tracking_number: trackingNumber.trim(), shipping_company: shippingCompany.trim() }
            : null
        );
        setShowTrackingModal(false);
        alert('✅ Kargo takip numarası başarıyla kaydedildi!');
      } else {
        throw new Error(data.error || 'Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Kargo takip numarası kaydedilirken hata:', error);
      alert(`❌ Hata: ${error instanceof Error ? error.message : 'Kargo takip numarası kaydedilemedi'}`);
    } finally {
      setSavingTracking(false);
    }
  };

  const openTrackingModal = () => {
    if (!selectedOrder) return;
    setTrackingNumber(selectedOrder?.tracking_number || '');
    setShippingCompany(selectedOrder?.shipping_company || '');
    setShowTrackingModal(true);
  };

  const openDesignModal = () => {
    setDesignFile(null);
    setAdminApproveDesign(true);
    setOrderPrice(selectedOrder?.price?.toString() || '');
    setProductTitle(selectedOrder?.product_title || '');
    setProductDescription(selectedOrder?.product_description || '');
    setShowDesignModal(true);
  };

  const handleImageSubmit = async () => {
    if (!selectedOrderForImage) return;

    try {
      setUploadingImage(true);
      let finalImageUrl = imageUrl;

      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${selectedOrderForImage}-${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage.from('approved-designs').upload(filePath, imageFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage.from('approved-designs').getPublicUrl(filePath);
        finalImageUrl = publicUrl;
      }

      if (!finalImageUrl) {
        alert('Lütfen bir görsel yükleyin veya URL girin');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-orders`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            orderId: selectedOrderForImage,
            approvedImageUrl: finalImageUrl,
          }),
        }
      );

      if (!response.ok) throw new Error('Görsel güncellenemedi');

      await fetchOrders();
      setShowImageModal(false);
      setSelectedOrderForImage(null);
      setImageUrl('');
      setImageFile(null);
    } catch (error) {
      console.error('Error:', error);
      alert('Görsel eklenirken bir hata oluştu');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (orderId: string) => {
    if (!confirm('Görseli silmek istediğinizden emin misiniz?')) return;

    try {
      const order = orders.find(o => o.id === orderId);
      if (order?.approved_image_url) {
        const urlParts = order.approved_image_url.split('/');
        const fileName = urlParts[urlParts.length - 1];

        await supabase.storage.from('approved-designs').remove([fileName]);
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-orders`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            orderId,
            approvedImageUrl: null,
          }),
        }
      );

      if (!response.ok) throw new Error('Görsel silinemedi');

      await fetchOrders();
    } catch (error) {
      console.error('Error:', error);
      alert('Görsel silinirken bir hata oluştu');
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedOrder) return;

    setConfirmingPayment(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'confirmPayment',
            orderId: selectedOrder.id,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        await fetchOrders();
        alert('✅ Ödeme onaylandı!');
      }
    } catch (error) {
      console.error('Ödeme onaylanırken hata:', error);
      alert('Ödeme onaylanırken bir hata oluştu');
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!selectedOrder) return;

    setRejectingPayment(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'rejectPayment',
            orderId: selectedOrder.id,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        await fetchOrders();
        alert('Ödeme bildirimi reddedildi');
      }
    } catch (error) {
      console.error('Ödeme bildirimi reddedilirken hata:', error);
      alert('Ödeme bildirimi reddedilirken bir hata oluştu');
    } finally {
      setRejectingPayment(false);
    }
  };

  const handleMarkAsDelivered = async () => {
    if (!selectedOrder) return;

    setDeliveringOrder(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'markAsDelivered',
            orderId: selectedOrder.id,
            adminEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        await fetchOrders();
        alert('✅ Sipariş "Teslim Edildi" olarak işaretlendi. Müşteri değerlendirme yapabilir.');
      }
    } catch (error) {
      console.error('Sipariş teslim edildi olarak işaretlenirken hata:', error);
      alert('İşlem sırasında bir hata oluştu');
    } finally {
      setDeliveringOrder(false);
    }
  };

  useEffect(() => {
    if (selectedOrder) {
      setOrderPrice(selectedOrder.price?.toString() || '');
      setProductTitle(selectedOrder.product_title || '');
      setProductDescription(selectedOrder.product_description || '');
    }
  }, [selectedOrder]);

  const fetchRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const { data, error } = await supabase
        .from('recommendations')
        .select('*')
        .eq('admin_email', adminEmail)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setRecommendations(data);
    } catch (error) {
      console.error('Öneriler yüklenirken hata:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const openRecommendationModal = (recommendation?: Recommendation) => {
    if (recommendation) {
      setEditingRecommendation(recommendation);
      setRecommendationTitle(recommendation.title);
      setRecommendationDescription(recommendation.description);
      setRecommendationPrice(recommendation.price.toString());
      setRecommendationImageFile(null);
    } else {
      setEditingRecommendation(null);
      setRecommendationTitle('');
      setRecommendationDescription('');
      setRecommendationPrice('');
      setRecommendationImageFile(null);
    }
    setShowRecommendationModal(true);
  };

  const handleSaveRecommendation = async () => {
    if (!recommendationTitle.trim() || !recommendationPrice.trim()) {
      alert('Lütfen ürün başlığı ve fiyat alanlarını doldurun');
      return;
    }

    const priceValue = parseFloat(recommendationPrice);
    if (isNaN(priceValue) || priceValue < 0) {
      alert('Lütfen geçerli bir fiyat girin');
      return;
    }

    setSavingRecommendation(true);
    try {
      let imageUrl = editingRecommendation?.image_url || null;

      // Yeni görsel yükleme
      if (recommendationImageFile) {
        const fileExt = recommendationImageFile.name.split('.').pop();
        const fileName = `recommendation-${Date.now()}.${fileExt}`;

        // Eski görseli sil
        if (editingRecommendation?.image_url) {
          const urlParts = editingRecommendation.image_url.split('/');
          const oldFileName = urlParts[urlParts.length - 1];
          try {
            await supabase.storage.from('approved-designs').remove([oldFileName]);
          } catch (err) {
            console.log('Eski görsel silinemedi:', err);
          }
        }

        const { error: uploadError } = await supabase.storage
          .from('approved-designs')
          .upload(fileName, recommendationImageFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('approved-designs')
          .getPublicUrl(fileName);

        imageUrl = publicUrl;
      }

      if (editingRecommendation) {
        // Güncelleme
        const { error } = await supabase
          .from('recommendations')
          .update({
            title: recommendationTitle.trim(),
            description: recommendationDescription.trim(),
            price: priceValue,
            image_url: imageUrl,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingRecommendation.id);

        if (error) throw error;
        alert('✅ Öneri güncellendi!');
      } else {
        // Yeni ekleme
        const { error } = await supabase
          .from('recommendations')
          .insert({
            admin_email: adminEmail,
            title: recommendationTitle.trim(),
            description: recommendationDescription.trim(),
            price: priceValue,
            image_url: imageUrl,
          });

        if (error) throw error;
        alert('✅ Öneri eklendi!');
      }

      await fetchRecommendations();
      setShowRecommendationModal(false);
    } catch (error) {
      console.error('Öneri kaydedilirken hata:', error);
      alert('Öneri kaydedilirken bir hata oluştu');
    } finally {
      setSavingRecommendation(false);
    }
  };

  const handleDeleteRecommendation = async (recommendationId: string) => {
    setDeletingRecommendation(true);
    try {
      const recommendation = recommendations.find(r => r.id === recommendationId);
      
      // Görseli sil
      if (recommendation?.image_url) {
        const urlParts = recommendation.image_url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        try {
          await supabase.storage.from('approved-designs').remove([fileName]);
        } catch (err) {
          console.log('Görsel silinemedi:', err);
        }
      }

      const { error } = await supabase
        .from('recommendations')
        .delete()
        .eq('id', recommendationId);

      if (error) throw error;

      await fetchRecommendations();
      setDeleteRecommendationConfirm(null);
      alert('✅ Öneri silindi!');
    } catch (error) {
      console.error('Öneri silinirken hata:', error);
      alert('Öneri silinirken bir hata oluştu');
    } finally {
      setDeletingRecommendation(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col lg:flex-row">
      {/* Mobil Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png"
            alt="Kartvizitler Logo"
            className="h-10 w-auto object-contain"
          />
          <span className="text-gray-900 font-semibold">Yönetici Paneli</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
        >
          <i className={`text-xl ${sidebarOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Sol Sidebar */}
      <aside className={`${
        sidebarOpen ? 'fixed inset-0 z-50' : 'hidden'
      } lg:relative lg:flex w-full lg:w-64 bg-white border-r border-gray-200 flex-col`}>
        <div className="p-5 border-b border-gray-200 hidden lg:block">
          <div className="flex flex-col items-center gap-2">
            <img
              src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png"
              alt="Kartvizitler Logo"
              className="h-16 w-auto object-contain"
            />
            <span className="text-gray-900 font-semibold text-lg">Sipariş Takip</span>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            <li>
              <button
                onClick={() => {
                  setActiveTab('all');
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setShowSurveys(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  activeTab === 'all' && !showLiveSupport && !showCustomers && !showDelivered && !showSurveys
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-list-check text-xl"></i>
                <span className="text-sm font-medium">Tüm Siparişler</span>
                <span className="ml-auto bg-gray-100 text-gray-700 text-xs rounded-full animate-pulse">{stats.total}</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setShowCustomers(true);
                  setShowLiveSupport(false);
                  setShowDelivered(false);
                  setShowSurveys(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  showCustomers
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-user-line text-xl"></i>
                <span className="text-sm font-medium">Müşteriler</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setShowDelivered(true);
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowSurveys(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  showDelivered
                    ? 'bg-green-50 text-green-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-truck-line text-xl"></i>
                <span className="text-sm font-medium">Teslim Edilenler</span>
                {stats.delivered > 0 && (
                  <span className="ml-auto bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">{stats.delivered}</span>
                )}
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setShowLiveSupport(true);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setShowSurveys(false);
                  setLiveSupportLoading(true);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  showLiveSupport
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-customer-service-2-line text-xl"></i>
                <span className="text-sm font-medium">Canlı Destek</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setActiveTab('payment_notified');
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setShowSurveys(false);
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              >
                <i className="ri-bank-line text-xl"></i>
                <span className="text-sm font-medium">Ödeme Bildirimi</span>
                {stats.paymentNotified > 0 && (
                  <span className="ml-auto bg-blue-500 text-white text-xs px-2 py-1 rounded-full animate-pulse" title="Ödeme bildirimi yapıldı">
                    <i className="ri-bank-line mr-1"></i>
                    {stats.paymentNotified}
                  </span>
                )}
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setActiveTab('pending');
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setShowSurveys(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  activeTab === 'pending' && !showLiveSupport && !showCustomers && !showDelivered && !showSurveys
                    ? 'bg-amber-50 text-amber-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-time-line text-xl"></i>
                <span className="text-sm font-medium">Bekleyenler</span>
                <span className="ml-auto bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full">{stats.pending}</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setActiveTab('in_progress');
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setShowSurveys(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  activeTab === 'in_progress' && !showLiveSupport && !showCustomers && !showDelivered && !showSurveys
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-loader-4-line text-xl"></i>
                <span className="text-sm font-medium">İşlemde</span>
                <span className="ml-auto bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">{stats.inProgress}</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setShowSurveys(true);
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  showSurveys
                    ? 'bg-violet-50 text-violet-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-survey-line text-xl"></i>
                <span className="text-sm font-medium">Anketler</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setShowQuotes(true);
                  setShowSurveys(false);
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setShowRecommendations(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  showQuotes
                    ? 'bg-purple-50 text-purple-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-price-tag-3-line text-xl"></i>
                <span className="text-sm font-medium">Teklifler</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setShowRecommendations(true);
                  setShowQuotes(false);
                  setShowSurveys(false);
                  setShowLiveSupport(false);
                  setShowCustomers(false);
                  setShowDelivered(false);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                  showRecommendations
                    ? 'bg-amber-50 text-amber-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <i className="ri-lightbulb-line text-xl"></i>
                <span className="text-sm font-medium">Öneriler</span>
              </button>
            </li>
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => {
              setShowPasswordModal(true);
              setSidebarOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-lock-password-line text-xl"></i>
            <span className="text-sm font-medium">Şifre Değiştir</span>
          </button>
          <a
            href="https://xkartvizitler.com/siparis-takibi/admin-paytr-settings"
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all cursor-pointer mt-1"
          >
            <i className="ri-bank-card-line text-xl"></i>
            <span className="text-sm font-medium">PayTR Ayarları</span>
          </a>
          <a
            href="https://xkartvizitler.com/siparis-takibi/admin-bank-settings"
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all cursor-pointer mt-1"
          >
            <i className="ri-bank-line text-xl"></i>
            <span className="text-sm font-medium">Banka Ayarları</span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer mt-1"
          >
            <i className="ri-logout-box-line text-xl"></i>
            <span className="text-sm font-medium">Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Ana İçerik */}
      {showRecommendations ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg">
                <i className="ri-lightbulb-line text-lg text-amber-600"></i>
              </div>
              <div>
                <h1 className="text-gray-900 font-semibold text-base lg:text-lg">Öneriler</h1>
                <p className="text-gray-500 text-sm">{recommendations.length} öneri</p>
              </div>
            </div>
            <button
              onClick={() => openRecommendationModal()}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Yeni Öneri
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            {loadingRecommendations ? (
              <div className="flex items-center justify-center py-20">
                <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin"></i>
              </div>
            ) : recommendations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <i className="ri-lightbulb-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-sm mb-2">Henüz öneri eklenmedi</p>
                <p className="text-gray-400 text-xs mt-1">Müşterilere önermek istediğiniz ürünleri ekleyin</p>
              </div>
            ) : (
              <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendations.map((recommendation) => (
                  <div
                    key={recommendation.id}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  >
                    {recommendation.image_url && (
                      <div className="w-full h-48 bg-gray-100">
                        <img
                          src={recommendation.image_url}
                          alt={recommendation.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="text-gray-900 font-semibold text-base mb-2">{recommendation.title}</h3>
                      {recommendation.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{recommendation.description}</p>
                      )}
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-amber-600 font-bold text-lg">{recommendation.price.toFixed(2)} ₺</span>
                        <span className="text-gray-400 text-xs">
                          {new Date(recommendation.created_at).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openRecommendationModal(recommendation)}
                          className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-1.5 whitespace-nowrap"
                        >
                          <i className="ri-edit-line"></i>
                          Düzenle
                        </button>
                        <button
                          onClick={() => setDeleteRecommendationConfirm(recommendation)}
                          className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                        >
                          <i className="ri-delete-bin-line"></i>
                          Sil
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : showQuotes ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-purple-100 rounded-lg">
                <i className="ri-price-tag-3-line text-lg text-purple-600"></i>
              </div>
              <div>
                <h1 className="text-gray-900 font-semibold text-base lg:text-lg">Teklifler</h1>
                <p className="text-gray-500 text-sm mb-2">{quotes.length} teklif</p>
                <p className="text-gray-400 text-xs mt-1">Yeni teklif oluşturmak için yukarıdaki butona tıklayın</p>
              </div>
            </div>
            <button
              onClick={() => openQuoteModal()}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Yeni Teklif
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            {loadingQuotes ? (
              <div className="flex items-center justify-center py-20">
                <i className="ri-loader-4-line text-3xl text-purple-500 animate-spin"></i>
              </div>
            ) : quotes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <i className="ri-price-tag-3-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-sm mb-2">Henüz teklif oluşturulmadı</p>
                <p className="text-gray-400 text-xs mt-1">Yeni teklif oluşturmak için yukarıdaki butona tıklayın</p>
              </div>
            ) : (
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4">
                {quotes.map((quote) => (
                  <div
                    key={quote.id}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow p-5"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-gray-900 font-semibold text-base mb-1">{quote.title}</h3>
                        {quote.description && (
                          <p className="text-gray-600 text-sm mb-2">{quote.description}</p>
                        )}
                      </div>
                      <span className={`ml-3 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getQuoteStatusColor(quote.status)}`}>
                        {getQuoteStatusLabel(quote.status)}
                      </span>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                      <div className="space-y-1.5">
                        {quote.items.map((item, index) => (
                          <div key={index} className="text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-700">{(item.name || item.product_name || '-')} x{item.quantity}</span>
                              <span className="text-gray-900 font-medium">{item.total.toFixed(2)} ₺</span>
                            </div>
                            {item.description && (
                              <p className="text-gray-500 text-xs mt-0.5 ml-2">{item.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-gray-300 mt-2 pt-2 flex items-center justify-between">
                        <span className="text-gray-700 font-medium text-sm">Toplam</span>
                        <span className="text-purple-600 font-bold text-lg">{quote.total_price.toFixed(2)} ₺</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-3">
                      {quote.status === 'accepted' && quote.customer_email && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                          <i className="ri-checkbox-circle-fill text-green-600"></i>
                          <span className="text-green-800 text-xs font-semibold">Kabul Eden: {quote.customer_email}</span>
                        </div>
                      )}
                      {quote.customer_email && quote.status !== 'accepted' && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <i className="ri-user-line"></i>
                          <span>{quote.customer_email}</span>
                        </div>
                      )}
                      {quote.valid_until && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <i className="ri-calendar-line"></i>
                          <span>Geçerlilik: {new Date(quote.valid_until).toLocaleDateString('tr-TR')}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <i className="ri-time-line"></i>
                        <span>{new Date(quote.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => copyQuoteLink(quote.share_id)}
                        className="flex-1 px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className={copiedQuoteId === quote.share_id ? 'ri-check-line' : 'ri-link'}></i>
                        {copiedQuoteId === quote.share_id ? 'Kopyalandı!' : 'Linki Kopyala'}
                      </button>
                      <button
                        onClick={() => openQuoteModal(quote)}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>
                        Düzenle
                      </button>
                      <button
                        onClick={() => setDeleteQuoteConfirm(quote)}
                        className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line"></i>
                        Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : showSurveys ? (
        <SurveysPanel adminEmail={adminEmail || ''} />
      ) : showCustomers ? (
        <CustomersPanel />
      ) : showDelivered ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-lg">
                <i className="ri-truck-line text-lg text-green-600"></i>
              </div>
              <div>
                <h1 className="text-gray-900 font-semibold text-base lg:text-lg">Teslim Edilenler</h1>
                <p className="text-gray-500 text-xs">{deliveredOrders.length} sipariş teslim edildi</p>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            {deliveredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <i className="ri-truck-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-sm mb-2">Henüz teslim edilen sipariş yok</p>
                <p className="text-gray-400 text-xs mt-1">Kargolandı durumundaki siparişleri &quot;Teslim Edildi&quot; olarak işaretleyebilirsiniz</p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-3">
                {deliveredOrders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Üst Kısım - Tıklanabilir */}
                    <button
                      onClick={() => setExpandedDeliveredId(expandedDeliveredId === order.id ? null : order.id)}
                      className="w-full p-4 lg:p-5 text-left cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-sm">
                            {order.customer_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="text-gray-900 font-semibold text-sm lg:text-base truncate">{order.customer_name}</h3>
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full whitespace-nowrap">
                                <i className="ri-checkbox-circle-fill"></i>
                                {order.status === 'shipped' ? 'Kargolandı' : 'Teslim Edildi'}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteDeliveredConfirm(order);
                                }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                                title="Sil"
                              >
                                <i className="ri-delete-bin-line text-base"></i>
                              </button>
                              <i className={`ri-arrow-${expandedDeliveredId === order.id ? 'up' : 'down'}-s-line text-gray-400 text-lg transition-transform`}></i>
                            </div>
                          </div>
                          <p className="text-gray-500 text-xs mb-2">{order.customer_email}</p>
                          <p className="text-gray-700 text-sm mb-3">{order.order_title}</p>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <i className="ri-calendar-line"></i>
                              Sipariş: {formatDate(order.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-hashtag"></i>
                              #{order.id.slice(0, 8).toUpperCase()}
                            </span>
                            {order.tracking_number && (
                              <span className="flex items-center gap-1">
                                <i className="ri-truck-line"></i>
                                {order.shipping_company && `${order.shipping_company}: `}{order.tracking_number}
                              </span>
                            )}
                            {order.price && (
                              <span className="flex items-center gap-1 font-medium text-gray-700">
                                <i className="ri-money-dollar-circle-line"></i>
                                {order.price.toFixed(2)} ₺
                              </span>
                            )}
                            {order.payment_confirmed && (
                              <span className="flex items-center gap-1 text-green-600 font-medium">
                                <i className="ri-checkbox-circle-fill"></i>
                                Ödendi
                              </span>
                            )}
                          </div>
                          {order.product_title && (
                            <div className="mt-2 px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg">
                              <p className="text-gray-800 text-xs font-medium">{order.product_title}</p>
                              {order.product_description && (
                                <p className="text-gray-500 text-xs mt-1">{order.product_description}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Genişletilmiş Detay Alanı */}
                    {expandedDeliveredId === order.id && (
                      <div className="border-t border-gray-200 p-4 lg:p-5 space-y-4 bg-gray-50/50">
                        {/* Onaylanan Tasarım Görseli */}
                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                          <h4 className="text-gray-900 font-medium text-sm flex items-center gap-2 mb-3">
                            <i className="ri-image-line text-green-500"></i>
                            Onaylanan Tasarım Görseli
                          </h4>
                          {order.approved_design_url ? (
                            <div className="space-y-3">
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <img
                                  src={order.approved_design_url}
                                  alt="Onaylanan Tasarım"
                                  className="w-full max-h-72 object-contain rounded-lg"
                                />
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {order.design_approved_by_admin && (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                    <i className="ri-checkbox-circle-fill"></i>
                                    Yönetici Onayladı
                                  </span>
                                )}
                                {order.design_approved_by_customer && (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                    <i className="ri-user-follow-fill"></i>
                                    Müşteri Onayladı
                                    {order.customer_approval_date && (
                                      <span className="ml-1 text-emerald-500">
                                        ({new Date(order.customer_approval_date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })})
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                              <i className="ri-image-line text-2xl text-gray-300 mb-2"></i>
                              <p className="text-gray-500 text-sm">Tasarım görseli eklenmemiş</p>
                            </div>
                          )}
                        </div>

                        {/* Son Görüşmeler (Düzenleme Talepleri) */}
                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                          <h4 className="text-gray-900 font-medium text-sm flex items-center gap-2 mb-3">
                            <i className="ri-chat-3-line text-orange-500"></i>
                            Son Görüşmeler
                          </h4>
                          <AdminRevisionChat
                            orderId={order.id}
                            adminEmail={adminEmail || ''}
                            customerName={order.customer_name}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : showLiveSupport ? (
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Üst Bar */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg">
                <i className="ri-customer-service-2-line text-lg text-emerald-600"></i>
              </div>
              <div>
                <h1 className="text-gray-900 font-semibold text-base lg:text-lg mb-3 lg:mb-4">Canlı Destek Yönetimi</h1>
                <p className="text-gray-500 text-xs">Müşteri destek taleplerini yönetin</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-xs font-medium animate-pulse">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                Aktif
              </span>
            </div>
          </div>

          {/* Loading Overlay */}
          {liveSupportLoading && (
            <div className="absolute inset-0 bg-white z-10 flex items-center justify-center">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 text-sm font-medium">Canlı Destek yükleniyor...</p>
                <p className="text-gray-400 text-xs mt-1">Lütfen bekleyin</p>
              </div>
            </div>
          )}

          {/* iframe */}
          <iframe
            src="https://xkartvizitler.com/supportboard/admin.php"
            className="flex-1 w-full border-0"
            onLoad={() => setLiveSupportLoading(false)}
            title="Canlı Destek Paneli"
            allow="microphone; camera; clipboard-write"
          />
        </div>
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Sipariş Listesi - Önceki tam hali korundu */}
          <div className="w-full lg:w-96 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-col max-h-[40vh] lg:max-h-none">
            <div className="p-4 lg:p-5 border-b border-gray-200">
              <h2 className="text-gray-900 font-semibold text-base lg:text-lg mb-3 lg:mb-4">Müşteri Listesi</h2>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Müşteri ara..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <i className="ri-loader-4-line text-3xl text-blue-500 animate-spin"></i>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-8 lg:py-12">
                  <i className="ri-inbox-line text-4xl lg:text-5xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500 text-sm">Sipariş bulunamadı</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredOrders.map((order) => (
                    <div
                      key={order.id}
                      className={`relative group ${
                        selectedOrder?.id === order.id
                          ? 'bg-blue-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="w-full p-3 lg:p-4 text-left transition-all cursor-pointer pr-12"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-medium text-sm">
                              {order.customer_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-gray-900 font-medium text-sm truncate">{order.customer_name}</h3>
                              <span className="text-gray-500 text-xs">{formatTime(order.created_at)}</span>
                            </div>
                            <p className="text-gray-600 text-xs truncate mb-2">{order.order_title}</p>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${getStatusColor(order.status)}`}></span>
                              <span className="text-gray-500 text-xs">{getStatusLabel(order.status)}</span>
                              {order.admin_note && (
                                <i className="ri-sticky-note-line text-amber-500 text-xs ml-1" title="Not var"></i>
                              )}
                              {order.payment_notified && !order.payment_confirmed && (
                                <span className="ml-1 flex items-center gap-1 bg-blue-500 text-white rounded-full text-xs font-medium animate-pulse" title="Ödeme bildirimi yapıldı">
                                  <i className="ri-bank-line"></i>
                                  <span>Ödeme Bildirimi!</span>
                                </span>
                              )}
                              {order.payment_confirmed && (
                                <span className="ml-1 flex items-center gap-1 bg-green-100 text-green-700 rounded-full text-xs font-medium" title="Ödeme onaylandı">
                                  <i className="ri-checkbox-circle-line"></i>
                                  <span>Ödendi</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(order);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer opacity-0 group-hover:opacity-100"
                        title="Sil"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sipariş Detayı */}
          <div className="flex-1 bg-gray-50 flex flex-col overflow-hidden">
            {selectedOrder ? (
              <>
                <div className="p-4 lg:p-6 border-b border-gray-200 bg-white">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 lg:gap-4">
                      <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                        <span className="text-white font-semibold text-base lg:text-lg">
                          {selectedOrder.customer_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-gray-900 font-semibold text-lg lg:text-xl truncate">{selectedOrder.customer_name}</h2>
                        <p className="text-gray-600 text-sm">E-posta: {selectedOrder.customer_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={selectedOrder.status}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          handleStatusUpdate(selectedOrder.id, newStatus);
                        }}
                        className="flex-1 bg-white border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-xs lg:text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none cursor-pointer shadow-sm hover:border-gray-400 transition-colors"
                      >
                        <option value="pending">Tasarım Bilgileriniz Bekleniyor</option>
                        <option value="designer_review">Grafiker Kontrolünde</option>
                        <option value="design_in_progress">Tasarım Hazırlanıyor</option>
                        <option value="awaiting_approval">Tasarım Onayınız Bekleniyor</option>
                        <option value="sent_to_print">Üretimde</option>
                        <option value="shipped">Ürün Kargolandı</option>
                        <option value="cancelled">İptal</option>
                      </select>
                      {selectedOrder.status === 'shipped' && (
                        <button
                          onClick={handleMarkAsDelivered}
                          disabled={deliveringOrder}
                          className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs lg:text-sm font-medium rounded-lg transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2 whitespace-nowrap shadow-sm"
                        >
                          {deliveringOrder ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              İşleniyor...
                            </>
                          ) : (
                            <>
                              <i className="ri-checkbox-circle-line"></i>
                              Teslim Edildi
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 lg:p-6">
                  <div className="max-w-3xl space-y-4 lg:space-y-6">
                    {/* Ödeme Bildirimi Kartı */}
                    {selectedOrder.payment_notified && !selectedOrder.payment_confirmed && (
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-5 shadow-lg">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                            <i className="ri-bank-line text-3xl text-white"></i>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-semibold text-lg mb-1">Ödeme Bildirimi Yapıldı!</h3>
                            <p className="text-blue-100 text-sm mb-3">
                              Müşteri havale/EFT yaptığını bildirdi. Lütfen banka hesabınızı kontrol edin.
                            </p>
                            {selectedOrder.payment_notified_at && (
                              <p className="text-blue-200 text-xs mb-4">
                                Bildirim Tarihi: {new Date(selectedOrder.payment_notified_at).toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={handleConfirmPayment}
                                disabled={confirmingPayment}
                                className="px-4 py-2 bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                              >
                                {confirmingPayment ? (
                                  <>
                                    <i className="ri-loader-4-line animate-spin"></i>
                                    Onaylanıyor...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-checkbox-circle-line"></i>
                                    Ödemeyi Onayla
                                  </>
                                )}
                              </button>
                              <button
                                onClick={handleRejectPayment}
                                disabled={rejectingPayment}
                                className="px-4 py-2 bg-white/20 text-white font-medium rounded-lg hover:bg-white/30 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                              >
                                {rejectingPayment ? (
                                  <>
                                    <i className="ri-loader-4-line animate-spin"></i>
                                    İşleniyor...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-close-circle-line"></i>
                                    Bildirimi Reddet
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Ödeme Onaylandı Kartı */}
                    {selectedOrder.payment_confirmed && (
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-5 shadow-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-checkbox-circle-fill text-3xl text-white"></i>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-semibold text-lg mb-1">Ödeme Onaylandı</h3>
                            <p className="text-green-100 text-sm">
                              Bu sipariş için ödeme başarıyla onaylandı.
                            </p>
                            {selectedOrder.payment_confirmed_at && (
                              <p className="text-green-200 text-xs mt-2">
                                Onay Tarihi: {new Date(selectedOrder.payment_confirmed_at).toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bildirim Gönderme - E-posta yerine */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <h3 className="text-gray-900 font-medium flex items-center gap-2 text-sm lg:text-base">
                          <i className="ri-notification-3-line text-green-500"></i>
                          Müşteriye Bildirim Gönder
                        </h3>
                        <button
                          onClick={openNotificationModal}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
                        >
                          <i className="ri-send-plane-line"></i>
                          Bildirim Gönder
                        </button>
                      </div>
                      <p className="text-gray-500 text-sm lg:text-sm mt-2">
                        Müşterinin paneline anlık bildirim gönderin. Bildirimler müşteri panelinde görünecektir.
                      </p>
                    </div>

                    {/* Onaylanan Tasarım Görseli */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between mb-3 lg:mb-4">
                        <h3 className="text-gray-900 font-medium flex items-center gap-2 text-sm lg:text-base">
                          <i className="ri-image-line text-green-500"></i>
                          Onaylanan Tasarım Görseli
                        </h3>
                        <div className="flex items-center gap-2">
                          {selectedOrder.approved_design_url && (
                            <button
                              onClick={() => setDeleteDesignConfirm(true)}
                              className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-xs lg:text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                            >
                              <i className="ri-delete-bin-line"></i>
                              Sil
                            </button>
                          )}
                          <button
                            onClick={openDesignModal}
                            className="px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 text-xs lg:text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                          >
                            <i className={selectedOrder.approved_design_url ? 'ri-edit-line' : 'ri-add-line'}></i>
                            {selectedOrder.approved_design_url ? 'Düzenle' : 'Görsel Ekle'}
                          </button>
                        </div>
                      </div>

                      {/* Fiyat Bilgisi */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-blue-800 font-medium text-sm flex items-center gap-2">
                            <i className="ri-price-tag-3-line"></i>
                            Ürün ve Fiyat Bilgileri
                          </h4>
                          {selectedOrder.price && (
                            <span className="text-blue-900 font-bold text-lg">
                              {selectedOrder.price.toFixed(2)} ₺
                            </span>
                          )}
                        </div>
                        
                        {/* Ürün Başlığı */}
                        <div className="mb-3">
                          <label className="block text-blue-700 text-sm font-medium mb-1.5">
                            Ürün Başlığı (Sepette görünecek)
                          </label>
                          <input
                            type="text"
                            value={productTitle}
                            onChange={(e) => setProductTitle(e.target.value)}
                            placeholder="Örn: 1000 Adet Kartvizit"
                            className="w-full bg-white border border-blue-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            maxLength={200}
                          />
                        </div>

                        {/* Ürün Açıklaması */}
                        <div className="mb-3">
                          <label className="block text-blue-700 text-sm font-medium mb-1.5">
                            Ürün Açıklaması (Sepette görünecek)
                          </label>
                          <textarea
                            value={productDescription}
                            onChange={(e) => setProductDescription(e.target.value)}
                            placeholder="Örn: 350gr Kuşe, Mat Selefon, Çift Taraflı Baskı"
                            rows={2}
                            className="w-full bg-white border border-blue-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                            maxLength={500}
                          />
                          <div className="flex justify-end mt-1">
                            <span className="text-blue-500 text-xs">{productDescription.length}/200 karakter</span>
                          </div>
                        </div>

                        {/* Fiyat */}
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={orderPrice}
                              onChange={(e) => setOrderPrice(e.target.value)}
                              placeholder="Fiyat girin (TL)"
                              className="w-full bg-white border border-blue-300 rounded-lg pl-4 pr-12 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
                              TL
                            </span>
                          </div>
                          <button
                            onClick={handleSavePrice}
                            disabled={savingPrice || !orderPrice}
                            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                          >
                            {savingPrice ? (
                              <>
                                <i className="ri-loader-4-line animate-spin"></i>
                                Kaydediliyor...
                              </>
                            ) : (
                              <>
                                <i className="ri-save-line"></i>
                                Kaydet
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-blue-600 text-xs mt-2">
                          <i className="ri-information-line mr-1"></i>
                          Bu bilgiler müşteri sepetinde görünecektir
                        </p>

                        {/* Mevcut Ürün Bilgileri Gösterimi */}
                        {(selectedOrder.product_title || selectedOrder.product_description) && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <p className="text-blue-700 text-xs font-medium mb-2">Kayıtlı Ürün Bilgileri:</p>
                            {selectedOrder.product_title && (
                              <p className="text-gray-800 text-sm font-medium">{selectedOrder.product_title}</p>
                            )}
                            {selectedOrder.product_description && (
                              <p className="text-gray-600 text-xs mt-1">{selectedOrder.product_description}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {selectedOrder.approved_design_url ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 lg:p-4">
                          <img
                            src={selectedOrder.approved_design_url}
                            alt="Onaylanan Tasarım"
                            className="w-full max-h-64 object-contain rounded-lg mb-3"
                          />
                          {/* Onay Durumu Gösterimi */}
                          {selectedOrder.design_approved_by_admin ? (
                            <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2 text-green-700">
                                <i className="ri-checkbox-circle-fill text-lg"></i>
                                <span className="font-medium text-sm">Yönetici Tarafından Onaylandı</span>
                              </div>
                              <p className="text-green-600 text-xs mt-1">Bu tasarım baskıya hazır olarak işaretlendi</p>
                            </div>
                          ) : (
                            <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2 text-amber-700">
                                <i className="ri-time-line text-lg"></i>
                                <span className="font-medium text-sm">Müşteri Onayı Bekleniyor</span>
                              </div>
                              <p className="text-amber-600 text-xs mt-1">Müşteri panelinde onay butonu görünecektir</p>
                            </div>
                          )}
                          {/* Müşteri Onay Durumu */}
                          {selectedOrder.design_approved_by_customer && (
                            <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-3">
                              <div className="flex items-center gap-2 text-emerald-700">
                                <i className="ri-user-follow-fill text-lg"></i>
                                <span className="font-medium text-sm">Müşteri Tasarımı Onayladı</span>
                              </div>
                              {selectedOrder.customer_approval_date && (
                                <p className="text-emerald-600 text-xs mt-1">
                                  Onay Tarihi: {new Date(selectedOrder.customer_approval_date).toLocaleDateString('tr-TR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          )}
                          {!selectedOrder.design_approved_by_admin && !selectedOrder.design_approved_by_customer && (
                            <p className="text-gray-500 text-xs text-center">
                              Bu görsel müşteri panelinde görünecektir
                            </p>
                          )}

                          {/* Düzenleme Talepleri Yazışmaları */}
                          <AdminRevisionChat
                            orderId={selectedOrder.id}
                            adminEmail={adminEmail || ''}
                            customerName={selectedOrder.customer_name}
                          />
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 lg:p-4 text-center">
                          <i className="ri-image-add-line text-2xl text-gray-300 mb-2"></i>
                          <p className="text-gray-500 text-sm">Henüz tasarım görseli eklenmedi</p>
                          <p className="text-gray-400 text-xs mt-1">Baskıya alınan tasarımın görselini eklemek için &quot;Görsel Ekle&quot; butonuna tıklayın</p>
                        </div>
                      )}
                    </div>

                    {/* Kargo Takip Numarası */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between mb-3 lg:mb-4">
                        <h3 className="text-gray-900 font-medium flex items-center gap-2 text-sm lg:text-base">
                          <i className="ri-truck-line text-blue-500"></i>
                          Kargo Takip Numarası
                        </h3>
                        <button
                          onClick={openTrackingModal}
                          className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs lg:text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                        >
                          <i className={selectedOrder.tracking_number ? 'ri-edit-line' : 'ri-add-line'}></i>
                          {selectedOrder.tracking_number ? 'Düzenle' : 'Takip No Ekle'}
                        </button>
                      </div>
                      {selectedOrder.tracking_number ? (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 lg:p-4">
                          {selectedOrder.shipping_company && (
                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-blue-200">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-building-2-line text-lg text-blue-600"></i>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Kargo Firması</p>
                                <p className="text-gray-900 font-medium text-sm">{selectedOrder.shipping_company}</p>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-barcode-line text-xl text-blue-600"></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-500 text-xs">Takip Numarası</p>
                              <p className="text-gray-900 font-mono text-sm lg:text-base font-medium">{selectedOrder.tracking_number}</p>
                            </div>
                          </div>
                          <p className="text-blue-600 text-xs mt-2">
                            <i className="ri-information-line mr-1"></i>
                            Bu bilgiler müşteri panelinde görünecektir
                          </p>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 lg:p-4 text-center">
                          <i className="ri-truck-line text-2xl text-gray-300 mb-2"></i>
                          <p className="text-gray-500 text-sm">Henüz kargo takip numarası eklenmedi</p>
                          <p className="text-gray-400 text-xs mt-1">Kargo takip numarası eklemek için &quot;Takip No Ekle&quot; butonuna tıklayın</p>
                        </div>
                      )}
                    </div>

                    {/* Bilgilendirme Notu */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between mb-3 lg:mb-4">
                        <h3 className="text-gray-900 font-medium flex items-center gap-2 text-sm lg:text-base">
                          <i className="ri-sticky-note-line text-amber-500"></i>
                          Müşteriye Bilgilendirme Notu
                        </h3>
                        <button
                          onClick={openNoteModal}
                          className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs lg:text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                        >
                          <i className="ri-edit-line"></i>
                          {selectedOrder.admin_note ? 'Düzenle' : 'Not Ekle'}
                        </button>
                      </div>
                      {selectedOrder.admin_note ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 lg:p-4">
                          <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedOrder.admin_note}</p>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 lg:p-4 text-center">
                          <i className="ri-sticky-note-line text-2xl text-gray-300 mb-2"></i>
                          <p className="text-gray-500 text-sm">Henüz not eklenmedi</p>
                          <p className="text-gray-400 text-xs mt-1">Müşteriye bilgilendirme notu bırakmak için &quot;Not Ekle&quot; butonuna tıklayın</p>
                        </div>
                      )}
                    </div>

                    {/* Durum Kartı */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                        <i className="ri-route-line text-blue-600"></i>
                        Sipariş Durumu
                      </h3>
                      <div className="space-y-2 lg:space-y-3">
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            ['pending', 'designer_review', 'design_in_progress', 'awaiting_approval', 'sent_to_print', 'shipped'].includes(selectedOrder.status)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            <i className="ri-checkbox-circle-line text-base lg:text-lg"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900 text-xs lg:text-sm font-medium">Tasarım Bilgileriniz Bekleniyor</p>
                            <p className="text-gray-500 text-xs">Sipariş alındı</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            ['designer_review', 'design_in_progress', 'awaiting_approval', 'sent_to_print', 'shipped'].includes(selectedOrder.status)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            <i className="ri-user-search-line text-base lg:text-lg"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900 text-xs lg:text-sm font-medium">Grafiker Kontrolünde</p>
                            <p className="text-gray-500 text-xs">Tasarım inceleniyor</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            ['design_in_progress', 'awaiting_approval', 'sent_to_print', 'shipped'].includes(selectedOrder.status)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            <i className="ri-pencil-ruler-2-line text-base lg:text-lg"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900 text-xs lg:text-sm font-medium">Tasarım Hazırlanıyor</p>
                            <p className="text-gray-500 text-xs">Grafiker çalışıyor</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            ['awaiting_approval', 'sent_to_print', 'shipped'].includes(selectedOrder.status)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            <i className="ri-eye-line text-base lg:text-lg"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900 text-xs lg:text-sm font-medium">Tasarım Onayınız Bekleniyor</p>
                            <p className="text-gray-500 text-xs">Onay için hazır</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            ['sent_to_print', 'shipped'].includes(selectedOrder.status)
                              ? 'bg-emerald-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            <i className="ri-printer-line text-base lg:text-lg"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900 text-xs lg:text-sm font-medium">Üretimde</p>
                            <p className="text-gray-500 text-xs">Üretim aşamasında</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            selectedOrder.status === 'shipped'
                              ? 'bg-emerald-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            <i className="ri-truck-line text-base lg:text-lg"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900 text-xs lg:text-sm font-medium">Ürün Kargolandı</p>
                            <p className="text-gray-500 text-xs">Teslimata çıktı</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sipariş Bilgileri */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                        <i className="ri-file-list-3-line text-blue-600"></i>
                        Sipariş Bilgileri
                      </h3>
                      <div className="space-y-3 lg:space-y-4">
                        <div>
                          <label className="text-gray-500 text-xs uppercase tracking-wider">Sipariş Başlığı</label>
                          <p className="text-gray-900 mt-1 text-sm lg:text-base">{selectedOrder.order_title}</p>
                        </div>
                        {selectedOrder.order_description && (
                          <div>
                            <label className="text-gray-500 text-xs uppercase tracking-wider">Açıklama</label>
                            <p className="text-gray-700 mt-1 whitespace-pre-wrap text-sm lg:text-base">{selectedOrder.order_description}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                          <div>
                            <label className="text-gray-500 text-xs uppercase tracking-wider">Oluşturulma Tarihi</label>
                            <p className="text-gray-900 mt-1 text-sm lg:text-base">{formatDate(selectedOrder.created_at)} - {formatTime(selectedOrder.created_at)}</p>
                          </div>
                          <div>
                            <label className="text-gray-500 text-xs uppercase tracking-wider">Sipariş No</label>
                            <p className="text-gray-900 mt-1 font-mono text-xs lg:text-sm">#{selectedOrder.id.slice(0, 8).toUpperCase()}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dosyalar */}
                    {selectedOrder.file_urls && selectedOrder.file_urls.length > 0 && (
                      <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                        <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                          <i className="ri-attachment-line text-blue-600"></i>
                          Yüklenen Dosyalar ({selectedOrder.file_urls.length})
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {selectedOrder.file_urls.map((url, index) => {
                            const fileName = url.split('/').pop() || `dosya-${index + 1}`;
                            const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
                            return (
                              <a
                                key={index}
                                href={url}
                                download={fileName}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-all cursor-pointer group text-left"
                              >
                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200 flex-shrink-0">
                                  <i className={`text-xl text-gray-600 ${
                                    ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension) ? 'ri-image-line' :
                                    ['pdf'].includes(fileExtension) ? 'ri-file-pdf-line' :
                                    ['doc', 'docx'].includes(fileExtension) ? 'ri-file-word-line' :
                                    ['xls', 'xlsx'].includes(fileExtension) ? 'ri-file-excel-line' :
                                    ['zip', 'rar', '7z'].includes(fileExtension) ? 'ri-file-zip-line' :
                                    'ri-file-line'
                                  }`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 text-sm truncate">{fileName}</p>
                                  <p className="text-gray-500 text-xs">İndirmek için tıkla</p>
                                </div>
                                <i className="ri-download-line text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0"></i>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Müşteri İletişim */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                        <i className="ri-user-line text-green-600"></i>
                        Müşteri Bilgileri
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200 flex-shrink-0">
                            <i className="ri-mail-line text-xl text-gray-600"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-500 text-xs">E-posta</p>
                            <p className="text-gray-900 text-sm truncate">{selectedOrder.customer_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200 flex-shrink-0">
                            <i className="ri-calendar-line text-xl text-gray-600"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-500 text-xs">Kayıt Tarihi</p>
                            <p className="text-gray-900 text-sm">{formatDate(selectedOrder.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Müşteri Adres Bilgileri */}
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200 shadow-sm">
                      <h3 className="text-gray-900 font-medium mb-3 lg:mb-4 flex items-center gap-2 text-sm lg:text-base">
                        <i className="ri-map-pin-line text-green-600"></i>
                        Müşteri Adres Bilgileri
                      </h3>
                      {selectedOrder.customer_profile && (selectedOrder.customer_profile.shipping_address || selectedOrder.customer_profile.shipping_phone) ? (
                        <div className="space-y-4">
                          {/* Kargo Adresi */}
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="text-green-800 font-medium text-sm mb-3 flex items-center gap-2">
                              <i className="ri-truck-line"></i>
                              Kargo Adresi
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {selectedOrder.customer_profile.shipping_phone && (
                                <div>
                                  <p className="text-gray-500 text-xs">Telefon</p>
                                  <p className="text-gray-900 text-sm font-medium">{selectedOrder.customer_profile.shipping_phone}</p>
                                </div>
                              )}
                              {selectedOrder.customer_profile.shipping_address && (
                                <div className="sm:col-span-2">
                                  <p className="text-gray-500 text-xs">Adres</p>
                                  <p className="text-gray-900 text-sm">{selectedOrder.customer_profile.shipping_address}</p>
                                </div>
                              )}
                              {(selectedOrder.customer_profile.shipping_city || selectedOrder.customer_profile.shipping_district) && (
                                <div>
                                  <p className="text-gray-500 text-xs">İl / İlçe</p>
                                  <p className="text-gray-900 text-sm">
                                    {[selectedOrder.customer_profile.shipping_city, selectedOrder.customer_profile.shipping_district].filter(Boolean).join(' / ')}
                                  </p>
                                </div>
                              )}
                              {selectedOrder.customer_profile.shipping_postal_code && (
                                <div>
                                  <p className="text-gray-500 text-xs">Posta Kodu</p>
                                  <p className="text-gray-900 text-sm">{selectedOrder.customer_profile.shipping_postal_code}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Fatura Bilgileri */}
                          {selectedOrder.customer_profile.billing_same_as_shipping === false && selectedOrder.customer_profile.billing_address ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="text-blue-800 font-medium text-sm mb-3 flex items-center gap-2">
                                <i className="ri-file-list-3-line"></i>
                                Fatura Bilgileri
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {selectedOrder.customer_profile.billing_name && (
                                  <div className="sm:col-span-2">
                                    <p className="text-gray-500 text-xs">Ad / Ünvan</p>
                                    <p className="text-gray-900 text-sm font-medium">{selectedOrder.customer_profile.billing_name}</p>
                                  </div>
                                )}
                                {selectedOrder.customer_profile.billing_address && (
                                  <div className="sm:col-span-2">
                                    <p className="text-gray-500 text-xs">Adres</p>
                                    <p className="text-gray-900 text-sm">{selectedOrder.customer_profile.billing_address}</p>
                                  </div>
                                )}
                                {(selectedOrder.customer_profile.billing_city || selectedOrder.customer_profile.billing_district) && (
                                  <div>
                                    <p className="text-gray-500 text-xs">İl / İlçe</p>
                                    <p className="text-gray-900 text-sm">
                                      {[selectedOrder.customer_profile.billing_city, selectedOrder.customer_profile.billing_district].filter(Boolean).join(' / ')}
                                    </p>
                                  </div>
                                )}
                                {selectedOrder.customer_profile.billing_postal_code && (
                                  <div>
                                    <p className="text-gray-500 text-xs">Posta Kodu</p>
                                    <p className="text-gray-900 text-sm">{selectedOrder.customer_profile.billing_postal_code}</p>
                                  </div>
                                )}
                                {selectedOrder.customer_profile.billing_tax_office && (
                                  <div>
                                    <p className="text-gray-500 text-xs">Vergi Dairesi</p>
                                    <p className="text-gray-900 text-sm">{selectedOrder.customer_profile.billing_tax_office}</p>
                                  </div>
                                )}
                                {selectedOrder.customer_profile.billing_tax_number && (
                                  <div>
                                    <p className="text-gray-500 text-xs">Vergi No</p>
                                    <p className="text-gray-900 text-sm font-mono">{selectedOrder.customer_profile.billing_tax_number}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : selectedOrder.customer_profile.billing_same_as_shipping === true ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-blue-700 text-sm flex items-center gap-2">
                                <i className="ri-checkbox-circle-line"></i>
                                Fatura adresi kargo adresiyle aynı
                              </p>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                          <i className="ri-map-pin-line text-2xl text-gray-300 mb-2"></i>
                          <p className="text-gray-500 text-sm">Müşteri henüz adres bilgisi girmemiş</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-center">
                  <i className="ri-cursor-line text-5xl lg:text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500 text-sm lg:text-base">Detayları görmek için bir sipariş seçin</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Öneri Oluştur/Düzenle Modal */}
      {showRecommendationModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                <i className="ri-lightbulb-line text-amber-500"></i>
                {editingRecommendation ? 'Öneri Düzenle' : 'Yeni Öneri Ekle'}
              </h3>
              <button
                onClick={() => setShowRecommendationModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Ürün Başlığı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={recommendationTitle}
                  onChange={(e) => setRecommendationTitle(e.target.value)}
                  placeholder="Örn: Premium Kartvizit Paketi"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Açıklama
                </label>
                <textarea
                  value={recommendationDescription}
                  onChange={(e) => setRecommendationDescription(e.target.value)}
                  placeholder="Ürün hakkında detaylı açıklama..."
                  rows={3}
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none resize-none"
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Fiyat (TL) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={recommendationPrice}
                  onChange={(e) => setRecommendationPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Ürün Görseli
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setRecommendationImageFile(file);
                      }
                    }}
                    className="hidden"
                    id="recommendation-image-input"
                  />
                  <label
                    htmlFor="recommendation-image-input"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-colors"
                  >
                    <i className="ri-upload-cloud-line text-xl text-gray-500"></i>
                    <span className="text-gray-600 text-sm">
                      {recommendationImageFile ? recommendationImageFile.name : editingRecommendation?.image_url ? 'Yeni görsel seçmek için tıklayın' : 'Görsel seçmek için tıklayın'}
                    </span>
                  </label>
                </div>
                {recommendationImageFile && (
                  <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-2 text-center">Önizleme</p>
                    <img
                      src={URL.createObjectURL(recommendationImageFile)}
                      alt="Önizleme"
                      className="w-full max-h-48 object-contain rounded-lg"
                    />
                  </div>
                )}
                {!recommendationImageFile && editingRecommendation?.image_url && (
                  <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-2 text-center">Mevcut Görsel</p>
                    <img
                      src={editingRecommendation.image_url}
                      alt="Mevcut"
                      className="w-full max-h-48 object-contain rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => setShowRecommendationModal(false)}
                disabled={savingRecommendation}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleSaveRecommendation}
                disabled={savingRecommendation || !recommendationTitle.trim() || !recommendationPrice.trim()}
                className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {savingRecommendation ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    {editingRecommendation ? 'Güncelle' : 'Ekle'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Öneri Silme Onay Modal */}
      {deleteRecommendationConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg text-center mb-2">
              Öneriyi Silmek İstediğinizden Emin Misiniz?
            </h3>
            <p className="text-gray-600 text-sm text-center mb-6">
              <span className="font-medium text-gray-900">{deleteRecommendationConfirm.title}</span> önerisi kalıcı olarak silinecektir. Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteRecommendationConfirm(null)}
                disabled={deletingRecommendation}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteRecommendation(deleteRecommendationConfirm.id)}
                disabled={deletingRecommendation}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deletingRecommendation ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teklif Oluştur/Düzenle Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                <i className="ri-price-tag-3-line text-purple-500"></i>
                {editingQuote ? 'Teklif Düzenle' : 'Yeni Teklif Oluştur'}
              </h3>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Teklif Başlığı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={quoteTitle}
                  onChange={(e) => setQuoteTitle(e.target.value)}
                  placeholder="Örn: 1000 Adet Kartvizit Teklifi"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Açıklama
                </label>
                <textarea
                  value={quoteDescription}
                  onChange={(e) => setQuoteDescription(e.target.value)}
                  placeholder="Teklif hakkında detaylı açıklama..."
                  rows={3}
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                  maxLength={500}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-gray-700 text-sm font-medium">
                    Ürün Kalemleri <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={addQuoteItem}
                    className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    Ürün Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {quoteItems.map((item, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="grid grid-cols-1 gap-2">
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-5">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => handleQuoteItemChange(index, 'name', e.target.value)}
                              placeholder="Ürün adı"
                              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            />
                          </div>
                          <div className="col-span-2">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleQuoteItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                              placeholder="Adet"
                              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            />
                          </div>
                          <div className="col-span-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleQuoteItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              placeholder="Birim fiyat"
                              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            />
                          </div>
                          <div className="col-span-2 flex items-center justify-between gap-2">
                            <span className="text-gray-900 font-medium text-sm whitespace-nowrap">{item.total.toFixed(2)} ₺</span>
                            {quoteItems.length > 1 && (
                              <button
                                onClick={() => removeQuoteItem(index)}
                                className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <textarea
                            value={item.description || ''}
                            onChange={(e) => handleQuoteItemChange(index, 'description', e.target.value)}
                            placeholder="Ürün açıklaması (opsiyonel)"
                            rows={2}
                            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                            maxLength={300}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
                  <span className="text-purple-800 font-medium">Toplam Tutar</span>
                  <span className="text-purple-600 font-bold text-lg">{calculateQuoteTotal(quoteItems).toFixed(2)} ₺</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Müşteri E-postası <span className="text-gray-500 font-normal">(Opsiyonel)</span>
                  </label>
                  <input
                    type="email"
                    value={quoteCustomerEmail}
                    onChange={(e) => setQuoteCustomerEmail(e.target.value)}
                    placeholder="musteri@example.com"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Geçerlilik Tarihi <span className="text-gray-500 font-normal">(Opsiyonel)</span>
                  </label>
                  <input
                    type="date"
                    value={quoteValidUntil}
                    onChange={(e) => setQuoteValidUntil(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => setShowQuoteModal(false)}
                disabled={savingQuote}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleSaveQuote}
                disabled={savingQuote || !quoteTitle.trim() || quoteItems.some(item => !item.name.trim())}
                className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {savingQuote ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    {editingQuote ? 'Güncelle' : 'Oluştur'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teklif Silme Onay Modal */}
      {deleteQuoteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg text-center mb-2">
              Teklifi Silmek İstediğinizden Emin Misiniz?
            </h3>
            <p className="text-gray-600 text-sm text-center mb-6">
              <span className="font-medium text-gray-900">{deleteQuoteConfirm.title}</span> teklifi kalıcı olarak silinecektir. Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteQuoteConfirm(null)}
                disabled={deletingQuote}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteQuote(deleteQuoteConfirm.id)}
                disabled={deletingQuote}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deletingQuote ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Not Ekleme/Düzenleme Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            {notificationSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-gray-900 font-semibold text-lg mb-2">Bildirim Gönderildi!</h3>
                <p className="text-gray-600 text-sm">
                  Bildirim başarıyla <span className="font-medium">{selectedOrder.customer_name}</span> adlı müşteriye gönderildi.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                    <i className="ri-notification-3-line text-green-500"></i>
                    Bildirim Gönder
                  </h3>
                  <button
                    onClick={() => setShowNoteModal(false)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium text-sm">
                        {selectedOrder.customer_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium text-sm">{selectedOrder.customer_name}</p>
                      <p className="text-gray-500 text-xs">{selectedOrder.customer_email}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Başlık</label>
                    <input
                      type="text"
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                      placeholder="Bildirim başlığı"
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Açıklama</label>
                    <textarea
                      value={notificationDescription}
                      onChange={(e) => setNotificationDescription(e.target.value)}
                      placeholder="Müşteriye göndermek istediğiniz mesajı yazın..."
                      className="w-full h-32 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none resize-none"
                      maxLength={500}
                    />
                    <div className="flex justify-end mt-1">
                      <span className="text-gray-400 text-xs">{notificationDescription.length}/500 karakter</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowNoteModal(false)}
                    disabled={sendingNotification}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSendNotification}
                    disabled={sendingNotification || !notificationTitle.trim() || !notificationDescription.trim()}
                    className="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {sendingNotification ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Gönder
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bildirim Gönderme Modal */}
      {showNotificationModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            {notificationSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-gray-900 font-semibold text-lg mb-2">Bildirim Gönderildi!</h3>
                <p className="text-gray-600 text-sm">
                  Bildirim başarıyla <span className="font-medium">{selectedOrder.customer_name}</span> adlı müşteriye gönderildi.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                    <i className="ri-notification-3-line text-green-500"></i>
                    Bildirim Gönder
                  </h3>
                  <button
                    onClick={() => setShowNotificationModal(false)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium text-sm">
                        {selectedOrder.customer_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium text-sm">{selectedOrder.customer_name}</p>
                      <p className="text-gray-500 text-xs">{selectedOrder.customer_email}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Başlık</label>
                    <input
                      type="text"
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                      placeholder="Bildirim başlığı"
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Açıklama</label>
                    <textarea
                      value={notificationDescription}
                      onChange={(e) => setNotificationDescription(e.target.value)}
                      placeholder="Müşteriye göndermek istediğiniz mesajı yazın..."
                      className="w-full h-32 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none resize-none"
                      maxLength={500}
                    />
                    <div className="flex justify-end mt-1">
                      <span className="text-gray-400 text-xs">{notificationDescription.length}/500 karakter</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowNotificationModal(false)}
                    disabled={sendingNotification}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSendNotification}
                    disabled={sendingNotification || !notificationTitle.trim() || !notificationDescription.trim()}
                    className="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {sendingNotification ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Gönder
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Onaylanan Tasarım Görseli Modal */}
      {showDesignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                <i className="ri-image-line text-green-500"></i>
                Onaylanan Tasarım Görseli
              </h3>
              <button
                onClick={() => {
                  setShowDesignModal(false);
                  setDesignFile(null);
                  setAdminApproveDesign(true);
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <p className="text-gray-600 text-sm mb-4">
              Baskıya alınan tasarımın görselini bilgisayarınızdan seçin. Bu görsel müşteri panelinde görünecektir.
            </p>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Görsel Seç
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setDesignFile(file);
                    }
                  }}
                  className="hidden"
                  id="design-file-input"
                />
                <label
                  htmlFor="design-file-input"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                >
                  <i className="ri-upload-cloud-line text-xl text-gray-500"></i>
                  <span className="text-gray-600 text-sm">
                    {designFile ? designFile.name : 'Görsel seçmek için tıklayın'}
                  </span>
                </label>
              </div>
              {designFile && (
                <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line"></i>
                  {designFile.name} seçildi
                </p>
              )}
            </div>

            {designFile && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 lg:p-4 mb-4">
                <p className="text-gray-500 text-xs mb-2 text-center">Önizleme</p>
                <img
                  src={URL.createObjectURL(designFile)}
                  alt="Önizleme"
                  className="w-full max-h-48 object-contain rounded-lg"
                />
              </div>
            )}

            {/* Onay Seçeneği */}
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-gray-700 text-sm font-medium mb-3">
                Onay Durumu
              </label>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="approvalType"
                    checked={adminApproveDesign}
                    onChange={() => setAdminApproveDesign(true)}
                    className="mt-1 w-4 h-4 text-green-600 cursor-pointer"
                  />
                  <div>
                    <span className="text-gray-900 text-sm font-medium">Kendim Onaylıyorum</span>
                    <p className="text-gray-500 text-xs mt-0.5">Tasarım direkt olarak &quot;Onaylanan Tasarım Görseli&quot; olarak görünecek</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="approvalType"
                    checked={!adminApproveDesign}
                    onChange={() => setAdminApproveDesign(false)}
                    className="mt-1 w-4 h-4 text-amber-600 cursor-pointer"
                  />
                  <div>
                    <span className="text-gray-900 text-sm font-medium">Müşteri Onayına Bırak</span>
                    <p className="text-gray-500 text-xs mt-0.5">Müşteri panelinde &quot;Tasarımı Onayla&quot; butonu görünecektir</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDesignModal(false);
                  setDesignFile(null);
                  setAdminApproveDesign(true);
                }}
                disabled={savingDesign}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleSaveDesign}
                disabled={savingDesign || !designFile}
                className="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {savingDesign ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-line"></i>
                    Yükle ve Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tasarım Silme Onay Modal */}
      {deleteDesignConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-image-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg text-center mb-2">
              Tasarım Görselini Silmek İstediğinizden Emin Misiniz?
            </h3>
            <p className="text-gray-600 text-sm text-center mb-6">
              Bu işlem sonucunda müşteri artık onaylanan tasarım görselini göremeyecektir.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteDesignConfirm(false)}
                disabled={deletingDesign}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteDesign}
                disabled={deletingDesign}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deletingDesign ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Görsel Ekleme Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-800">Onaylanan Tasarım Görseli</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Görsel Yükle
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImageFile(file);
                      setImageUrl('');
                    }
                  }}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                {imageFile && (
                  <p className="mt-2 text-sm text-teal-600">
                    ✓ {imageFile.name} seçildi
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-300"></div>
                <span className="text-sm text-slate-500">veya</span>
                <div className="flex-1 h-px bg-slate-300"></div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Görsel URL&apos;si
                </label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setImageFile(null);
                  }}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {(imageFile || imageUrl) && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Önizleme:</p>
                  <div className="relative w-full h-48 bg-slate-100 rounded-lg overflow-hidden">
                    <img
                      src={imageFile ? URL.createObjectURL(imageFile) : imageUrl}
                      alt="Önizleme"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setSelectedOrderForImage(null);
                  setImageUrl('');
                  setImageFile(null);
                }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
                disabled={uploadingImage}
              >
                İptal
              </button>
              <button
                onClick={handleImageSubmit}
                disabled={uploadingImage || (!imageFile && !imageUrl)}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {uploadingImage ? 'Yükleniyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kargo Takip Numarası Modal */}
      {showTrackingModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-900 font-medium text-lg flex items-center gap-2">
                <i className="ri-truck-line text-blue-500"></i>
                Kargo Takip Numarası
              </h3>
              <button
                onClick={() => setShowTrackingModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">
              Kargo takip numarasını girin. Bu bilgiler müşteri panelinde görünecektir.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Kargo Firması <span className="text-gray-500 font-normal">(Opsiyonel)</span>
                </label>
                <input
                  type="text"
                  value={shippingCompany}
                  onChange={(e) => setShippingCompany(e.target.value)}
                  placeholder="Örn: Yurtiçi Kargo, Aras Kargo, MNG Kargo"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Takip Numarası <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Kargo takip numarasını girin"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  maxLength={50}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowTrackingModal(false)}
                disabled={savingTracking}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleSaveTracking}
                disabled={savingTracking || !trackingNumber.trim()}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {savingTracking ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teslim Edilen Sipariş Silme Onay Modal */}
      {deleteDeliveredConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg text-center mb-2">
              Siparişi Silmek İstediğinizden Emin Misiniz?
            </h3>
            <p className="text-gray-600 text-sm text-center mb-6">
              <span className="font-medium text-gray-900">{deleteDeliveredConfirm.customer_name}</span> - <span className="font-medium text-gray-900">{deleteDeliveredConfirm.order_title}</span> siparişi ve tüm ilişkili kayıtları kalıcı olarak silinecektir. Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteDeliveredConfirm(null)}
                disabled={deletingDelivered}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteDeliveredOrder(deleteDeliveredConfirm.id)}
                disabled={deletingDelivered}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deletingDelivered ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sipariş Silme Onay Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg text-center mb-2">
              Siparişi Silmek İstediğinizden Emin Misiniz?
            </h3>
            <p className="text-gray-600 text-sm text-center mb-6">
              <span className="font-medium text-gray-900">{deleteConfirm.customer_name}</span> - <span className="font-medium text-gray-900">{deleteConfirm.order_title}</span> siparişi kalıcı olarak silinecektir.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteOrder(deleteConfirm.id)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;